import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./PropertyDetails.css";
import Navigation from "../Components/Navigation/Navigation";
import Footer from "../Components/Navigation/Footer";
import BathIcon from "../Assets/Images/bath_icon.svg";
import BedIcon from "../Assets/Images/bed_icon.svg";
import BackArrow from "../Assets/Images/back-arrow.svg";
import ShareModal from "../Components/ShareModal";
import FloorplansModal from "../Components/FloorplansModal";

const PropertyDetails = () => {
  const { property_ref } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isShareOpen, setIsShareOpen] = useState(false);

  useEffect(() => {
    console.log("Property Ref from URL:", property_ref);

    if (!property_ref) {
      setError("Invalid property reference.");
      setLoading(false);
      return;
    }

    setProperty(null);
    setLoading(true);
    setError(null);

    const fetchPropertyDetails = async () => {
      try {
        const url = `https://webapi.10ninety.co.uk/properties/${property_ref}`;
        console.log("Fetching property details from:", url);

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "10ninety-webapi-key": process.env.REACT_APP_API_KEY,
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(`Failed to fetch: ${response.status} - ${errorData}`);
        }

        const data = await response.json();
        console.log("API Response:", data);

        if (!data.property_ref) {
          throw new Error(
            `No property found with the reference: ${property_ref}`
          );
        }

        setProperty(data);
      } catch (error) {
        console.error("Error fetching property:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchPropertyTypes = async () => {
      try {
        const response = await fetch(
          "https://webapi.10ninety.co.uk/property_types/",
          {
            method: "GET",
            headers: {
              "10ninety-webapi-key": process.env.REACT_APP_API_KEY,
              Accept: "application/json",
            },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch property types.");
        const data = await response.json();

        setPropertyTypes(data.property_types);
        console.log("Fetched Property Types:", data.property_types); // Log fetched data
      } catch (error) {
        console.error("Error fetching property types:", error);
      }
    };

    fetchPropertyDetails();
    fetchPropertyTypes();
  }, [property_ref]);

  // ✅ Log propertyTypes outside useEffect to check state updates
  useEffect(() => {
    console.log("Property Type ID from Property:", property?.property_type_id);
    console.log("Property Types List:", propertyTypes);
  }, [property, propertyTypes]);

  const cleanSummary = property?.description
    ? property.description
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/&pound;/gi, "£")
    : "";

  const formatPrice = (price) =>
    price ? `£${price.toLocaleString()}` : "Price not available";

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="error-message">Error: {error}</div>;
  if (!property) return <div>No property found.</div>;

  const nextImage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === property.images?.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? property.images?.length - 1 : prevIndex - 1
    );
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  const propertyType = propertyTypes.find(
    (type) => type.id === property?.prop_sub_id
  );

  console.log("Floorplans data:", property.floorplans);


  return (
    <div className="propertyDetailsContainer">
      <Navigation linkColor="black" submenuColor="black" menuIconColor="black"/>
      <div className="content">
        <div className="leftSection">
          <div className="backToSearchLink">
            <button onClick={() => navigate(-1)} className="backButton">
              <img src={BackArrow} alt="Back Arrow" /> Back to Search
            </button>
          </div>
          <div className="gallerySection">
            <div className="carouselContainer">
              {property.images?.length > 0 ? (
                <>
                  <div className="carouselImages">
                    <img
                      src={property.images[currentIndex]?.url}
                      alt={`Property Image ${currentIndex + 1}`}
                      className="carouselImg"
                    />
                  </div>
                  <button onClick={prevImage} className="arrow leftArrow">
                    &#8249;
                  </button>
                  <button onClick={nextImage} className="arrow rightArrow">
                    &#8250;
                  </button>
                </>
              ) : (
                <p>No images available</p>
              )}
            </div>
            <div className="thumbnailsContainer">
              {property.images?.map((image, index) => (
                <img
                  key={index}
                  src={image.url}
                  alt={`Thumbnail ${index + 1}`}
                  className={`thumbnail ${
                    currentIndex === index ? "active" : ""
                  }`}
                  onClick={() => handleThumbnailClick(index)}
                />
              ))}
            </div>
          </div>
          <div className="propertyDescription">
            <h1>About This Property</h1>
            {cleanSummary?.split("\n").map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
       
        </div>
        <div className="stickyCard">
          <div className="priceShareContainer">
            <h2 className="propertyPrice">{formatPrice(property.price)}</h2>
            <ShareModal />
          </div>

          {/* Display property type only if available */}
          <h4>
            {property.bedrooms || 0} bedroom
            {propertyType ? ` ${propertyType.name.toLowerCase()}` : ""}
          </h4>

          <p>
            {[

              property.address_1
                ?.replace(/\b(Flat|Apt|Apartment)\s*\d*[A-Za-z]?\b/gi, "")
                .replace(/^\d+[A-Za-z]?\s*/, "")
                .trim(),

              property.address_2,

              property.town && !property.county
                ? property.town
                : property.county,

              property.postcode_1,
            ]
              .filter(Boolean)
              .join(", ")
              .replace(/^,\s*/, "") || "Address not available"}
          </p>

          <div className="propertyCardDetails">
            <span>
              {property.bedrooms || 0}
              <img className="propertyCardIcons" src={BedIcon} alt="Bed Icon" />
            </span>
            <span>
              {property.bathrooms || 0}
              <img
                className="propertyCardIcons"
                src={BathIcon}
                alt="Bath Icon"
              />
            </span>
          </div>
          <div className="keyFeatures">
            <h5>Key Features</h5>
            <ul>
              {property.features?.length > 0 ? (
                property.features
                  .slice(0, 4)
                  .map((feature, index) => <li key={index}>{feature}</li>)
              ) : (
                <li>No features listed</li>
              )}
            </ul>
          </div>
          <div className="floorplanLayout">
          <FloorplansModal floorplans={property.floorplans} />
          </div>
          <div className="smallContact">
            <h5>Contact</h5>
            <p>020 8191 0907</p>
          </div>
          <button className="enquireButton">
            <a href="/contact-us">Arrange a viewing</a>
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PropertyDetails;
